import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useOnboarding } from "context/OnboardingContext";
import { onboardingService } from "services/onboarding-service";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import { useMaterialUIController } from "context";
import { getOnboardingThemeTokens, withAlpha } from "./themeTokens";
import { motion } from "framer-motion";
import brandWhite from "assets/images/logos/svg/White logo - no background.svg";
import brandDark from "assets/images/logos/svg/Black logo - no background.svg";
import { CircularProgress } from "@mui/material";

const LandingPage = () => {
  const { claim } = useParams();
  const navigate = useNavigate();
  const { updateSessionToken, updateSessionId, setTheme, setClaimData, setClaimMeta } = useOnboarding();
  const [verification, setVerification] = useState("");
  const [loading, setLoading] = useState(false);
  const [theme, setLocalTheme] = useState(null);
  const [controller] = useMaterialUIController();
  const { transparentSidenav, whiteSidenav, darkMode } = controller;

  const t = getOnboardingThemeTokens({ darkMode, templateTheme: theme });

  useEffect(() => {
    if (claim) {
      onboardingService.trackClick(claim, "direct").catch(console.error);
      onboardingService
        .getPublicTemplate(claim)
        .then((res) => {
          const templateData = res.data || res;
          setLocalTheme(templateData);
          setTheme(templateData);
        })
        .catch((err) => console.error("Failed to fetch template", err));
    }
  }, [claim, setTheme]);

  const handleStart = async () => {
    if (!verification || verification.length < 4) {
      return;
    }
    setLoading(true);
    try {
      const res = await onboardingService.startSession(claim, verification);
      updateSessionToken(res.data.session_token);
      updateSessionId(res.data.session_id);
      // Store claim data and meta for later steps
      setClaimData(res.data);
      setClaimMeta({
        claim_type: res.data.claim_type,
        build_id: res.data.build?.build_id || null,
        device_id: res.data.device?.device_id || null,
      });
      navigate("/onboard/entity-review");
    } catch (err) {
      console.error('handle start session error', err)
      if (err.message) {
        alert(err.message);
      } else {
        alert("An error occurred while starting the session");
      }
    } finally {
      setLoading(false);
    }
  };

  const primaryColor = theme?.payload?.primaryColor || "#344767";
  const backgroundColor = theme?.payload?.backgroundColor || "#f0f2f5";

  if (!theme) {
    return (
      <MDBox
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{ background: t.canvas }}
      >
        <CircularProgress color="info" />
        <MDTypography variant="button" fontWeight="medium" sx={{ mt: 2 }}>
          Setting things up...
        </MDTypography>
      </MDBox>
    );
  }

  return (
    <MDBox
      component={motion.div}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      position="relative"
      overflow="hidden"
      sx={{
        background: `linear-gradient(135deg, ${t.background} 0%, ${t.gradientEnd} 100%)`,
        perspective: "1000px",
      }}
    >
      {/* Decorative Background Elements */}
      <MDBox
        component={motion.div}
        animate={{
          scale: [1, 1.2, 1],
          rotate: [0, 90, 0],
          x: [0, 50, 0],
          y: [0, -30, 0],
        }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        position="absolute"
        top="-10%"
        left="-10%"
        width="40vw"
        height="40vw"
        borderRadius="50%"
        sx={{
          background: `radial-gradient(circle, ${withAlpha(t.primary, 0.15)} 0%, transparent 70%)`,
          filter: "blur(60px)",
          zIndex: 0,
        }}
      />
      <MDBox
        component={motion.div}
        animate={{
          scale: [1, 1.3, 1],
          rotate: [0, -90, 0],
          x: [0, -50, 0],
          y: [0, 40, 0],
        }}
        transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
        position="absolute"
        bottom="-15%"
        right="-10%"
        width="50vw"
        height="50vw"
        borderRadius="50%"
        sx={{
          background: `radial-gradient(circle, ${withAlpha(t.primary, 0.10)} 0%, transparent 70%)`,
          filter: "blur(80px)",
          zIndex: 0,
        }}
      />

      <MDBox
        component={motion.div}
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.6, ease: "easeOut" }}
        maxWidth="480px"
        width="90%"
        p={5}
        position="relative"
        zIndex={1}
        sx={{
          background: t.panelBg,
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          borderRadius: "24px",
          border: "1px solid rgba(255, 255, 255, 0.3)",
          boxShadow: darkMode
            ? "0 8px 32px 0 rgba(0, 0, 0, 0.37)"
            : "0 8px 32px 0 rgba(31, 38, 135, 0.1)",
          textAlign: "center",
        }}
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.5 }}
        >
          <MDBox mb={4} display="flex" justifyContent="center">
            <img
              src={(transparentSidenav && !darkMode) || whiteSidenav ? brandWhite : brandDark}
              alt="Rivio Logo"
              style={{
                maxWidth: "200px",
                height: "auto",
                filter: darkMode ? "drop-shadow(0 0 8px rgba(255,255,255,0.2))" : "none",
              }}
            />
          </MDBox>
        </motion.div>

        <motion.div
          initial={{ x: -10, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.5 }}
        >
          <MDTypography variant="h3" fontWeight="bold" mb={1} sx={{ color: darkMode ? "#fff" : "#1e293b" }}>
            Welcome to Rivio
          </MDTypography>
          <MDTypography variant="body1" color={darkMode ? "secondary" : "text"} mb={4} sx={{ opacity: 0.8 }}>
            Complete your onboarding in just a few steps.
          </MDTypography>
        </motion.div>

        <MDBox
          component={motion.div}
          initial={{ y: 10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.5 }}
          mb={4}
          p={2}
          borderRadius="12px"
          sx={{
            background: darkMode ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.03)",
            border: `1px dashed ${primaryColor}40`,
          }}
        >
          <MDTypography variant="button" color="text" sx={{ display: "block", mb: 0.5 }}>
            CLAIM CODE
          </MDTypography>
          <MDTypography variant="h5" fontWeight="bold" sx={{ color: primaryColor, letterSpacing: "2px" }}>
            {claim}
          </MDTypography>
        </MDBox>

        <MDBox
          component={motion.div}
          initial={{ y: 10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.7, duration: 0.5 }}
          mb={4}
        >
          <MDInput
            label="Last 4 digits of Order Ref"
            value={verification}
            onChange={(e) => setVerification(e.target.value)}
            fullWidth
            variant="outlined"
            placeholder="e.g. 1234"
            inputProps={{ maxLength: 4 }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                backgroundColor: darkMode ? "rgba(0,0,0,0.1)" : "#fff",
              },
            }}
          />
        </MDBox>

        <motion.div
          initial={{ y: 10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.5 }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <MDButton
            variant="gradient"
            color="info"
            fullWidth
            onClick={handleStart}
            disabled={loading || verification.length < 4}
            size="large"
            sx={{
              borderRadius: "12px",
              padding: "12px",
              fontSize: "1rem",
              textTransform: "none",
              boxShadow: "0 4px 14px 0 rgba(0, 118, 255, 0.39)",
            }}
          >
            {loading ? (
              <MDBox display="flex" alignItems="center">
                <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                Starting Onboarding...
              </MDBox>
            ) : (
              "Get Started"
            )}
          </MDButton>
        </motion.div>

        <MDBox mt={4}>
          <MDTypography variant="caption" color="secondary" sx={{ opacity: 0.7 }}>
            Secure onboarding powered by Rivio.io
          </MDTypography>
        </MDBox>
      </MDBox>
    </MDBox>
  );
};

export default LandingPage;
